import type { User } from "@firebase/auth";
import React, {
  useContext,
  createContext,
  useEffect,
  useState,
  useCallback,
  PropsWithChildren
} from "react";
import { createSocket, Socket } from "../socket";
import { useAuth } from "./AuthContext";

export const SocketContext = createContext<{ socket: Socket | null }>({
  socket: null
});

type DebouncedFunction<T extends (...args: any[]) => void> = ((
  ...args: Parameters<T>
) => void) & {
  cancel: () => void;
};

function debounce<T extends (...args: any[]) => void>(
  fn: T,
  waitMs: number
): DebouncedFunction<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = null;
      fn(...args);
    }, waitMs);
  }) as DebouncedFunction<T>;

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}

// Debounce socket reconnection to prevent rapid reconnects
const RECONNECT_DEBOUNCE_MS = 1000;
const TOKEN_REFRESH_INTERVAL_MS = 55 * 60 * 1000; // 55 minutes

export const SocketProvider: React.FC<PropsWithChildren> = (props) => {
  const [_, forceUpdate] = useState(0);
  const [_socket, setSocket] = useState<Socket | null>(null);
  const auth = useAuth();

  // Debounced reconnection handler
  const handleReconnect = useCallback(
    debounce(async (socket: Socket, newToken: string | undefined) => {
      if (!newToken) {
        console.warn("Attempted reconnect with no token");
        return;
      }

      try {
        socket.auth = { token: newToken };
        console.warn("[Debug] Socket reconnecting with new token");
        const nextS = socket.disconnect().connect();
        if (!socket.connected) {
          console.warn("Socket reconnect before initial connection!");
        }
        setSocket(nextS);
      } catch (err) {
        console.error("Failed to reconnect socket:", err);
      }
    }, RECONNECT_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    const unsubs: Array<() => void> = [];
    let refreshInterval: NodeJS.Timeout | null = null;

    async function run() {
      if (auth.isSignedIn && !_socket) {
        try {
          const token = await auth.getIdToken();
          const socket = createSocket(token);

          socket.onAny((event, ...args) => {
            console.warn(`[Debug] Socket event: ${event}`, ...args);
          });

          socket.on("error", (msg) => console.error(msg));

          // Token change handler
          const handleIdTokenChanged = async (nextUser: User | null) => {
            try {
              const nextToken = await nextUser?.getIdToken();
              console.warn("[Debug] IdToken changed triggered refresh");
              await handleReconnect(socket, nextToken);
            } catch (err) {
              console.error("Failed to handle token change:", err);
            }
          };

          // Proactive token refresh
          const refreshToken = async () => {
            try {
              const newToken = await auth.getIdToken(true);
              console.warn("[Debug] Proactive token refresh");
              await handleReconnect(socket, newToken);
            } catch (err) {
              console.error("Failed to refresh token:", err);
            }
          };

          // Set up proactive refresh interval
          refreshInterval = setInterval(
            refreshToken,
            TOKEN_REFRESH_INTERVAL_MS
          );

          unsubs.push(auth.onIdTokenChanged(handleIdTokenChanged));
          unsubs.push(() => {
            if (refreshInterval) {
              clearInterval(refreshInterval);
            }
            handleReconnect.cancel();
          });

          socket.on("connect", () => {
            console.warn("[Debug] Connect event fired");
            socket.once("authenticated", () => {
              console.warn("[Debug] Authenticated event fired");
              forceUpdate(Date.now());
            });
            socket.once("unauthorised", async () => {
              try {
                console.warn("[Debug] Unauthorized - forcing token refresh");
                await auth.getIdToken(true);
              } catch (err) {
                console.error(
                  "Failed to refresh token after unauthorized:",
                  err
                );
              }
            });
          });

          setSocket(socket);
        } catch (err) {
          console.error("Failed to initialize socket:", err);
        }
      }
    }

    run();
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      unsubs.forEach((fn) => fn());
    };
  }, [auth.isSignedIn, handleReconnect]);

  return <SocketContext.Provider {...props} value={{ socket: _socket }} />;
};

export const useSocket = (
  handler?: (socket: Socket) => (() => void) | undefined
) => {
  const { socket } = useContext(SocketContext);
  useEffect(() => {
    if (socket && handler) {
      const fn = handler(socket);
      return () => {
        return fn ? fn() : undefined;
      };
    }
  }, [(socket?.auth as { [key: string]: string })?.token]);
  return socket;
};
